import {
  PublicWaitlist,
  PublicWaitlistCustomText,
  PublicWaitlistFieldLabels,
  PublicWaitlistFieldOptions,
  PublicWaitlistRequiredFields,
  PublicWaitlistTheme,
} from '@shared/restaurant/models';
import { StringKeyDictionary } from '@shared/types';
import { DefaultPage } from '@shared/restaurant/enums';
import { OptionItem, OptionsObject, PublicPageTab } from './models';
import { FieldOption } from './models/field-option';
import { PublicPagesTabsDataParams } from './models/pages-tabs-data';
import { ImageDisplayOption } from './models/image-display';
import { getDefaultCustomText } from '@tablesready/frontend-store';

const tablesReadyFields: OptionItem[] = [
  {
    name: 'Size of party',
    id: 'size',
    sharedName: 'size',
  },
  {
    name: 'Name',
    id: 'name',
    sharedName: 'name',
  },
];

export function getFieldsList(): OptionItem[] {
  const fields: OptionItem[] = [
    {
      name: 'Phone',
      id: 'phone',
      sharedName: 'phone',
    },
    {
      name: 'Special request/notes',
      id: 'note',
      sharedName: 'note',
    },
  ];

  fields.unshift(...tablesReadyFields);

  return fields;
}

// eslint-disable-next-line max-lines-per-function
export function getViewOption (): OptionsObject {
  return {
    name: {
      defaultValue: true,
      sharedName: 'name',
    },
    size: {
      name: 'Size of Party in Waitlist View',
      id: 'size_view',
      defaultValue: false,
      sharedName: 'size',
    },
    phone: {
      name: 'Phone Number (last 4 digits)',
      id: 'phone',
      defaultValue: false,
      sharedName: 'phone',
    },
    note: {
      name: 'Special requests/Notes field in Waitlist View',
      id: 'note_view',
      defaultValue: false,
      sharedName: 'note',
    },
    services: {
      name: 'Services field in Waitlist View',
      id: 'showService',
      defaultValue: false,
      sharedName: 'services',
    },
    resources: {
      name: 'Resources field in Waitlist View',
      id: 'showResource',
      defaultValue: false,
      sharedName: 'resources',
    },
  };
}

// eslint-disable-next-line max-lines-per-function
export function getAddOptions (): OptionsObject {
  return {
    name: {
      defaultValue: true,
      sharedName: 'name',
    },
    phone: {
      id: 'phone_add',
      defaultValue: false,
      sharedName: 'phone',
    },
    size: {
      name: 'Size of Party in Waitlist Add',
      id: 'size_add',
      defaultValue: false,
      sharedName: 'size',
    },
    note: {
      name: 'Special requests/Notes field in Waitlist Add',
      id: 'note_add',
      defaultValue: false,
      sharedName: 'note',
    },
    services: {
      name: 'Show Services Selection Drop-down',
      id: 'showServicesCheckIn',
      defaultValue: false,
      sharedName: 'services',
    },
    resources: {
      name: 'Show Resources Selection Drop-down',
      id: 'showResourcesCheckIn',
      defaultValue: false,
      sharedName: 'resources',
    },
  };
}

export const WAITLIST_FIELD_SETTING_MAX_TIME_DEFAULT = 99;
export const WAITLIST_FIELD_OPTIONS_DEFAULT: PublicWaitlistFieldOptions = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  first_character: false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  size_view: false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  size_add: true,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  note_view: false,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  note_add: true,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  actual_wait: true,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  estimated_wait: true,
  phone: true,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  phone_add: true,
  noteOptions: [],
  columns: true,
  position: false,
  striped: true,
  upcoming: false,
  showService: false,
  showResource: false,
  showResourcesCheckIn: false,
  showServicesCheckIn: true,
  arrivalTime: false,
  quotedTime: false,
};

export const WAITLIST_FIELD_OPTIONS_REQUIRED_FIELDS_DEFAULT = ():
PublicWaitlistRequiredFields => ({
  name: true,
  size: false,
  phone: false,
  note: false,
  resources: false,
  services: false,
});

export const WAITLIST_FIELD_OPTIONS_LABELS_DEFAULT = (): PublicWaitlistFieldLabels => ({
  name: 'Name',
  note: 'Special requests',
  size: 'Party size',
  phone: 'Phone number',
  deliveryId: 'Delivery ID',
  truck: 'Truck #',
  carrier: 'Carrier',
});

export const WAITLIST_FIELD_OPTIONS_CUSTOM_TEXT_DEFAULT = (username: string): PublicWaitlistCustomText => (getDefaultCustomText(username));

export const CUSTOM_TEXT_PAGES = (reservationPlural: string) => ([
  {
    name: 'Waitlist',
    value: 'waitlist',
  },
  {
    name: reservationPlural,
    value: 'bookings'
  },
  {
    name: 'Footer',
    value: 'footer'
  },
  {
    name: 'Out of business hours',
    value: 'closed'
  },
]);

export const WAITLIST_FIELD_SETTINGS_THEME_DEFAULT: PublicWaitlistTheme = {
  backgroundColor: '#191c37',
  header1Color: '#ffffff',
  header2Color: '#ffffff',
  header3Color: '#ffffff',
  estimatedWaitColor: '#ffffff',
  footerColor: '#ffffff',
  bodyColor: '#191c37',
  tableHeaderColor: '#191c37',
  addButtonBackgroundColor: '#2daf17',
  addButtonColor: '#ffffff',
  formBackgroundColor: '#ffffff',
  highlightColor: '#c1c3d3',
};

export const DEFAULT_PUBLIC_WAITING_LIST_VALUE: PublicWaitlist = {
  defaultPage: DefaultPage.waitlist,
  theme: WAITLIST_FIELD_SETTINGS_THEME_DEFAULT,
  fieldOptions: WAITLIST_FIELD_OPTIONS_DEFAULT,
  fieldSettings: { maxSize: WAITLIST_FIELD_SETTING_MAX_TIME_DEFAULT },
  checkinCap: {
    cap: 0,
    capStart: 0,
    enabled: false,
    message: '',
    reopenDuration: 0,
  },
  customMessage: {
    enabled: false,
    message: 'We\'re not currently on a wait. Come on in!',
  },
  showUpcomingReservations: true,
  allowReservationsCheckin: false,
  showOnlyCheckedInReservations: false,
  apiKey: '',
  api: false,
  enableReservationsCheckinVerification: false,
  view: false,
  add: false,
  redirect: '',
  image: '',
  position: '',
  schedule: [''],
  /* Undefined because they require input */
  requiredFields: undefined,
  customText: undefined,
  fieldLabels: undefined,
  geofence: {
    enabled: false,
    distance: 0,
    message: 'You must be within {distance} feet to access this page.',
    checkin: false,
    waitlist: false,
    reservation: false,
  },
};

export const PUBLIC_WAITLIST_FIELD_OPTIONS = (customerLabel: string, customerPlural: string): FieldOption[] => [
  {
    name: `Actual Wait Time for Each ${customerLabel}`,
    id: 'actual_wait',
  },
  {
    name: 'Quoted time in Waitlist View',
    id: 'quotedTime',
  },
  {
    name: 'Arrival time in Waitlist View',
    id: 'arrivalTime',
  },
  {
    name: 'Estimated Wait Time (top of screen)',
    id: 'estimated_wait',
  },
  {
    name: 'Column Names in Waitlist View',
    id: 'columns',
  },
  {
    name: 'Position in line',
    id: 'position',
  },
  {
    name: `"Upcoming ${customerPlural}" header`,
    id: 'upcoming',
  },
];

export const PUBLIC_PAGES_TABS_DATA = (params: PublicPagesTabsDataParams): StringKeyDictionary<PublicPageTab> => ({
  view: {
    name: 'Public Waitlist page',
    dropdownPageName: 'Waitlist View Page',
    directLink: params.publicWaitlistUrl,
    // eslint-disable-next-line max-len
    embedPageCode: `<iframe src="${params.publicWaitlistUrl}" width="100%" height="900px" allowfullscreen allowtransparency="true" frameborder="0" scrolling="no"></iframe>`,
    // eslint-disable-next-line max-len
    buttonCode: `<a style="color:#fff;background-color:#2daf17;border-top:10px solid #2daf17;border-right:18px solid #2daf17;border-bottom:10px solid #2daf17;border-left:18px solid #2daf17;display:inline-block;text-decoration:none;font-weight:700;border-radius:4px;box-sizing:border-box;font-family: 'Source Sans Pro',Helvetica,Arial,sans-serif; font-size: 16px;text-align: center;" href="${params.publicWaitlistUrl}" target="_blank">View Our Waitlist</a>`,
    qrCodeTagName: 'qr-waitlist',
    buttonText: 'View Our Waitlist',
  },
  add: {
    name: 'Public Check-in page',
    dropdownPageName: 'Check-in Page',
    directLink: params.publicCheckinUrl,
    // eslint-disable-next-line max-len
    embedPageCode: `<iframe src="${params.publicCheckinUrl}" width="100%" height="900px" allowfullscreen allowtransparency="true" frameborder="0" scrolling="no"></iframe>`,
    // eslint-disable-next-line max-len
    buttonCode: `<a style="color:#fff;background-color:#2daf17;border-top:10px solid #2daf17;border-right:18px solid #2daf17;border-bottom:10px solid #2daf17;border-left:18px solid #2daf17;display:inline-block;text-decoration:none;font-weight:700;border-radius:4px;box-sizing:border-box;font-family: 'Source Sans Pro',Helvetica,Arial,sans-serif; font-size: 16px;text-align: center;" href="${params.publicCheckinUrl}" target="_blank">Join Our Waitlist</a>`,
    qrCodeTagName: 'qr-check-in',
    buttonText: 'Join Our Waitlist',
  },
  onlineReservationsEnabled: {
    name: 'Public Booking page',
    dropdownPageName: `Online ${params.reservationPluralLabel[0].toUpperCase() + params.reservationPluralLabel.substring(1)} Page`,
    directLink: params.publicOnlineBookingUrl,
    // eslint-disable-next-line max-len
    embedPageCode: `<iframe src="${params.publicOnlineBookingUrl}" width="100%" height="900px" allowfullscreen allowtransparency="true" frameborder="0" scrolling="no"></iframe>`,
    // eslint-disable-next-line max-len
    buttonCode: `<a style="color:#fff;background-color:#2daf17;border-top:10px solid #2daf17;border-right:18px solid #2daf17;border-bottom:10px solid #2daf17;border-left:18px solid #2daf17;display:inline-block;text-decoration:none;font-weight:700;border-radius:4px;box-sizing:border-box;font-family: 'Source Sans Pro',Helvetica,Arial,sans-serif; font-size: 16px;text-align: center;" href="${params.publicOnlineBookingUrl}" target="_blank">${params.reservationButtonText}</a>`,
    qrCodeTagName: `qr-${params.reservationPluralLabel}`,
    buttonText: params.reservationButtonText,
  },
});

export const WAILIST_IMAGE_DISPLAY_OPTIONS: ImageDisplayOption[] = [
  {
    name: 'Select Image Display Location',
    value: '',
  },
  {
    name: 'Top',
    value: 'top',
  },
  {
    name: 'Bottom',
    value: 'bottom',
  },
];
